import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TagGraph from './landing/TagGraph';
import Demo from './landing/Demo';
import LearningLoop from './landing/LearningLoop';
import McpTabs from './landing/McpTabs';
import './LandingPage.css';

/* ─────────────────────────────────────────────────────────────
   Sections
   ───────────────────────────────────────────────────────────── */

function Nav() {
  return (
    <nav className="nav">
      <div className="wrap nav-inner">
        <Link to="/" className="brand">
          <span className="brand-mark">M</span>
          <span>MME</span>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              color: 'var(--ink-4)',
              marginLeft: 4,
            }}
          >
            railtech
          </span>
        </Link>
        <div className="nav-links">
          <a href="#how">How it works</a>
          <a href="#demo">Try it</a>
          <a href="#learning">Learning</a>
          <a href="#mcp">Connect</a>
          <a href="#python">Python SDK</a>
          <Link to="/documentation" className="nav-ghost">
            Docs
          </Link>
          <Link to="/login" className="nav-cta">
            Get API key →
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero no-border">
      <div className="wrap hero-grid">
        <div>
          <div className="hero-tag">
            <span className="dot" />
            <span>v0.1 · open beta</span>
          </div>
          <h1 className="hero-title">
            Memory that <em>learns</em> what actually helps your AI.
          </h1>
          <p className="hero-sub">
            MME is a bounded tag-graph memory engine. It saves what your agent
            learns, retrieves only what fits the budget, and quietly promotes
            the memories that keep getting used — no embeddings, no
            retraining, every result explainable.
          </p>
          <div className="hero-actions">
            <Link className="btn btn-primary" to="/login">
              Get your API key <span className="arrow">→</span>
            </Link>
            <a className="btn btn-ghost" href="#how">
              See how it works
            </a>
          </div>
          <div
            style={{
              marginTop: 40,
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              fontSize: 13,
              color: 'var(--ink-4)',
            }}
          >
            <span>· Native MCP for Claude &amp; Cursor</span>
            <span>· Python SDK with LangChain integration</span>
            <span>· Online learning, no retraining</span>
            <span>· Explainable retrieval</span>
          </div>
        </div>
        <TagGraph />
      </div>
    </section>
  );
}

function Problem() {
  const rows = [
    {
      n: '01',
      title: 'Your agent forgets across sessions.',
      sub: 'Every chat starts from zero. Users say "as I mentioned earlier" for the fifth time.',
      tag: 'context loss',
    },
    {
      n: '02',
      title: 'Vector search returns plausible noise.',
      sub: 'Cosine similarity finds shapes that rhyme with your query, not memories that actually apply.',
      tag: 'RAG drift',
    },
    {
      n: '03',
      title: 'Big context = big bill, slow response.',
      sub: 'Stuffing the whole history into every prompt costs tokens, latency, and attention.',
      tag: 'token bloat',
    },
  ];
  return (
    <section>
      <div className="wrap">
        <div className="eyebrow">The problem</div>
        <h2 className="section-title">
          Most memory is either <em>too much</em> or <em>wrong</em>.
        </h2>
        <p className="section-sub">
          Agent builders keep hitting the same three walls. MME is built
          around fixing each one.
        </p>
        <div style={{ marginTop: 8 }}>
          {rows.map((r) => (
            <div key={r.n} className="prob-row">
              <span className="prob-num">{r.n}</span>
              <div className="prob-text">
                <strong>{r.title}</strong>
                <span>{r.sub}</span>
              </div>
              <span className="prob-tag">{r.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: 'Step 01',
      t: 'Save context',
      d: 'Send any text — a preference, a decision, a fact. MME auto-extracts tags and stores the memory in your private graph.',
      code: `await mme.save({
  text: "Use Postgres for new services.",
  // tags are auto-extracted
})`,
    },
    {
      n: 'Step 02',
      t: 'Retrieve a pack',
      d: "Before each response, ask MME for a memory pack. It walks the graph from your query's seed tags, scores candidates, and packs to your token budget — exactly.",
      code: `const pack = await mme.inject({
  prompt: userMsg,
  tokenBudget: 1024,
})`,
    },
    {
      n: 'Step 03',
      t: 'Close the loop',
      d: 'Tell MME if the pack actually helped. Edges to useful memories strengthen (EMA); unused edges decay. The graph gets smarter without retraining.',
      code: `await mme.feedback({
  packId: pack.id,
  accepted: true,
})`,
    },
  ];
  return (
    <section id="how">
      <div className="wrap">
        <div className="eyebrow">How it works</div>
        <h2 className="section-title">Three calls. One memory graph.</h2>
        <p className="section-sub">
          Save, retrieve, feedback. No vectors to tune, no embeddings to host,
          no index to rebuild.
        </p>

        <div className="grid-3">
          {steps.map((s) => (
            <div key={s.n} className="card">
              <div className="how-index">{s.n}</div>
              <div className="how-title">{s.t}</div>
              <div className="how-desc" style={{ marginBottom: 20 }}>
                {s.d}
              </div>
              <pre className="code" style={{ fontSize: 12, padding: 16 }}>
                {s.code}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoSection() {
  return (
    <section id="demo">
      <div className="wrap">
        <div className="eyebrow">Try it</div>
        <h2 className="section-title">See a pack get built, live.</h2>
        <p className="section-sub">
          Pick a question your agent might get asked. Watch MME pick which
          memories fit, and why — right down to which tags overlapped.
        </p>
        <Demo />
      </div>
    </section>
  );
}

function LearningSection() {
  return (
    <section id="learning">
      <div className="wrap">
        <div className="eyebrow">Online learning</div>
        <h2 className="section-title">
          Useful memories rise. Stale ones <em>fade</em>.
        </h2>
        <p className="section-sub">
          Every pack returns with a feedback hook. When you mark a pack
          accepted, MME updates its edge weights via an exponential moving
          average — a small nudge each session, no retraining pipeline.
        </p>
        <LearningLoop />
      </div>
    </section>
  );
}

function Explainable() {
  const accent = { color: '#E8B97C' };
  const str = { color: '#B6D4A3' };
  const num = { color: '#9BB0D1' };
  return (
    <section>
      <div className="wrap">
        <div className="eyebrow">Explainable retrieval</div>
        <h2 className="section-title">
          Every memory comes with a <em>rationale</em>.
        </h2>
        <p className="section-sub">
          Unlike vector search, you can always answer "why did MME surface
          this memory?" — which seed tag matched, which edge it traversed,
          what its activation was. Useful for debugging, audit, and trust.
        </p>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              padding: '18px 24px',
              borderBottom: '1px solid var(--line)',
              background: 'var(--bg-2)',
              fontFamily: 'var(--mono)',
              fontSize: 12,
              color: 'var(--ink-3)',
            }}
          >
            GET /memory/inject — response
          </div>
          <pre className="code" style={{ margin: 0, borderRadius: 0 }}>
{`{
  "`}<span style={accent}>packId</span>{`": "`}<span style={str}>pk_01h9x4...</span>{`",
  "`}<span style={accent}>tokensUsed</span>{`": `}<span style={num}>58</span>{`,
  "`}<span style={accent}>tokenBudget</span>{`": `}<span style={num}>64</span>{`,
  "`}<span style={accent}>items</span>{`": [
    {
      "`}<span style={accent}>text</span>{`": "`}<span style={str}>OAuth2 PKCE flow is required for all public clients.</span>{`",
      "`}<span style={accent}>score</span>{`": `}<span style={num}>0.94</span>{`,
      "`}<span style={accent}>rationale</span>{`": {
        "`}<span style={accent}>seedHit</span>{`": ["`}<span style={str}>auth</span>{`", "`}<span style={str}>oauth2</span>{`"],
        "`}<span style={accent}>edgeWeight</span>{`": `}<span style={num}>0.88</span>{`,
        "`}<span style={accent}>recency</span>{`": `}<span style={num}>0.72</span>{`,
        "`}<span style={accent}>importance</span>{`": `}<span style={num}>0.80</span>{`
      }
    }
  ]
}`}
          </pre>
        </div>
      </div>
    </section>
  );
}

function McpSection() {
  const clients = [
    { name: 'Claude Desktop', sub: 'claude_desktop_config.json' },
    { name: 'Claude Code', sub: '~/.claude/mcp.json' },
    { name: 'Cursor', sub: '~/.cursor/mcp.json' },
    { name: 'Continue.dev', sub: '.continue/config.json' },
    { name: 'Windsurf', sub: 'mcp_config.json' },
  ];
  return (
    <section id="mcp">
      <div className="wrap">
        <div className="eyebrow">One memory, every tool</div>
        <h2 className="section-title">
          Works natively with the tools you already use.
        </h2>
        <p className="section-sub">
          MME speaks the Model Context Protocol, so every MCP-capable client
          can read and write the same memory graph.
        </p>

        <div className="grid-2" style={{ alignItems: 'start' }}>
          <McpTabs />
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="compat" style={{ gridTemplateColumns: '1fr' }}>
              {clients.map((c) => (
                <div
                  key={c.name}
                  className="compat-cell"
                  style={{
                    borderRight: 'none',
                    borderBottom: '1px solid var(--line)',
                    minHeight: 0,
                    padding: '14px 18px',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <strong>{c.name}</strong>
                    <div className="sub">{c.sub}</div>
                  </div>
                  <span
                    className="sub"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span
                      className="dot"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        background: 'var(--good)',
                      }}
                    />
                    connected
                  </span>
                </div>
              ))}
              <div
                style={{
                  padding: '12px 18px',
                  fontSize: 12,
                  color: 'var(--ink-4)',
                  fontFamily: 'var(--mono)',
                }}
              >
                … plus any future MCP-capable client.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PythonSdkSection() {
  return (
    <section id="python">
      <div className="wrap">
        <div className="eyebrow">Python SDK · v0.1.1 on PyPI</div>
        <h2 className="section-title">
          Now first-class in <em>Python</em>.
        </h2>
        <p className="section-sub">
          A thin, typed client over the MME REST API — sync and async, full
          Pydantic models, and a LangChain extra that drops into any LangChain
          or LangGraph agent. Designed to sit alongside your vector DB, not
          replace it.
        </p>

        <div
          className="grid-2"
          style={{ alignItems: 'stretch', marginTop: 8 }}
        >
          <div
            className="card"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div className="how-index">Step 01</div>
            <div className="how-title">Install</div>
            <div className="how-desc" style={{ marginBottom: 20 }}>
              One pip command. Python 3.9+. No native dependencies, no
              vector store to host.
            </div>
            <pre
              className="code"
              style={{ fontSize: 13, padding: 16, marginBottom: 18 }}
            >
{`pip install railtech-mme

# with the LangChain integration
pip install 'railtech-mme[langchain]'`}
            </pre>
            <div
              style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                marginTop: 'auto',
              }}
            >
              <a
                className="btn btn-primary"
                href="https://github.com/gokulJinu01/railtech-mme-python"
                target="_blank"
                rel="noreferrer"
              >
                GitHub <span className="arrow">→</span>
              </a>
              <a
                className="btn btn-ghost"
                href="https://pypi.org/project/railtech-mme/"
                target="_blank"
                rel="noreferrer"
              >
                PyPI
              </a>
            </div>
          </div>

          <div
            className="card"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div className="how-index">Step 02</div>
            <div className="how-title">Three calls. Same graph.</div>
            <div className="how-desc" style={{ marginBottom: 20 }}>
              The same save / inject / feedback surface as the REST API,
              with typed responses and proper exceptions.
            </div>
            <pre
              className="code"
              style={{ fontSize: 12, padding: 16, marginBottom: 0 }}
            >
{`from railtech_mme import MME

with MME() as mme:
    mme.save("I prefer dark chocolate.")
    mme.save("I'm allergic to peanuts.")

    pack = mme.inject(
        "What are my food preferences and allergies?",
        token_budget=1024,
    )

    mme.feedback(pack_id=pack.pack_id, accepted=True)`}
            </pre>
          </div>
        </div>

        <div
          style={{
            marginTop: 24,
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
            fontSize: 13,
            color: 'var(--ink-4)',
            fontFamily: 'var(--mono)',
          }}
        >
          <span>· sync + async clients</span>
          <span>· Pydantic models, mypy-strict</span>
          <span>· LangChain &amp; LangGraph tools</span>
          <span>· Python 3.9 / 3.10 / 3.11 / 3.12</span>
          <span>· Apache-2.0</span>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section>
      <div className="wrap">
        <div className="eyebrow">By the numbers</div>
        <h2 className="section-title">Bounded by design.</h2>
        <p className="section-sub">
          MME's graph walk has hard mathematical limits — so your memory layer
          can't silently blow up your latency or token budget.
        </p>
        <div className="stat-strip">
          <div className="stat-cell">
            <div className="stat-num">3</div>
            <div className="stat-label">API calls to integrate</div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                marginTop: 10,
              }}
            >
              save · inject · feedback
            </div>
          </div>
          <div className="stat-cell">
            <div className="stat-num">
              O<em>(</em>|S|·M<sup>D</sup>
              <em>)</em>
            </div>
            <div className="stat-label">Bounded complexity</div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                marginTop: 10,
              }}
            >
              M=32 · D=2 · B=128
            </div>
          </div>
          <div className="stat-cell">
            <div className="stat-num">0</div>
            <div className="stat-label">Embeddings required</div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                marginTop: 10,
              }}
            >
              tags, not vectors
            </div>
          </div>
          <div className="stat-cell">
            <div className="stat-num">MCP</div>
            <div className="stat-label">Native protocol</div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                marginTop: 10,
              }}
            >
              Claude · Cursor · more
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="no-border" style={{ paddingBottom: 80 }}>
      <div className="wrap">
        <div className="cta">
          <h2>
            Give your agent a memory it actually <em>earns</em>.
          </h2>
          <p>
            Free tier, no credit card. Save your first memory in under 5
            minutes.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Link className="btn btn-primary" to="/login">
              Get your API key <span className="arrow">→</span>
            </Link>
            <Link className="btn btn-ghost" to="/documentation">
              Read the docs
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap footer-inner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="brand-mark">M</span>
          <span style={{ color: 'var(--ink-2)', fontSize: 13 }}>
            MME — a <span style={{ color: 'var(--ink)' }}>Railtech</span>{' '}
            project
          </span>
        </div>
        <div className="footer-links">
          <Link to="/documentation">Docs</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:info@railtech.io">Contact</a>
          <span className="mono">© 2026</span>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────── */

export default function LandingPage() {
  // Reveal-on-scroll: any element with .reveal fades in when it enters view.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add('in')
        ),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="mme-landing">
      <Helmet>
        <title>MME — Memory that learns what helps your AI | Railtech</title>
        <meta
          name="description"
          content="MME is a bounded tag-graph memory engine for AI agents. Save, retrieve, and feedback in three calls. No embeddings, no retraining, every result explainable. Native MCP for Claude Desktop, Claude Code, and Cursor — plus an official Python SDK with LangChain integration (pip install railtech-mme)."
        />
        <meta
          name="keywords"
          content="MME, Memory Management Engine, AI memory, MCP, Model Context Protocol, Claude memory, Cursor memory, tag graph, agent memory, Railtech, Python SDK, railtech-mme, LangChain memory, LangGraph memory, agent memory python"
        />
        <meta
          property="og:title"
          content="MME — Memory that learns what helps your AI"
        />
        <meta
          property="og:description"
          content="Bounded tag-graph memory for AI agents. Save, retrieve, feedback — three calls, one graph. No embeddings. Native MCP, Python SDK, LangChain integration."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Nav />
      <Hero />
      <Problem />
      <HowItWorks />
      <DemoSection />
      <LearningSection />
      <Explainable />
      <McpSection />
      <PythonSdkSection />
      <Stats />
      <CTA />
      <Footer />
    </div>
  );
}
